import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Button, Result, Descriptions, Tag } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CONTACT_CONFIG } from '../../config/contact';

const QuestionnaireStatusPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { questionnaireId, submittedAt } = location.state || {};

  if (!questionnaireId) {
    return (
      <Result
        status="404"
        title={t('questionnaire:statusPage.notFound')}
        subTitle={t('questionnaire:statusPage.notFoundDesc')}
        extra={
          <Button type="primary" onClick={() => navigate('/questionnaire')}>
            {t('questionnaire:statusPage.refillButton')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Card>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            status="success"
            title={t('questionnaire:statusPage.submitSuccess')}
            subTitle={t('questionnaire:statusPage.submitSuccessDesc')}
            extra={[
              <Button
                type="primary"
                key="new"
                onClick={() => navigate('/questionnaire')}
              >
                {t('questionnaire:statusPage.newQuestionnaire')}
              </Button>,
              <Button
                key="contact"
                onClick={() =>
                  (window.location.href = `mailto:${CONTACT_CONFIG.supportEmail}`)
                }
              >
                {t('questionnaire:statusPage.contactUs')}
              </Button>,
            ]}
          />

          <div className="mt-8">
            <Descriptions
              title={t('questionnaire:statusPage.submissionDetails')}
              bordered
              column={2}
            >
              <Descriptions.Item
                label={t('questionnaire:statusPage.questionnaireId')}
                span={2}
              >
                <span className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {questionnaireId}
                </span>
              </Descriptions.Item>
              <Descriptions.Item
                label={t('questionnaire:statusPage.submitTime')}
              >
                {submittedAt
                  ? new Date(submittedAt).toLocaleString(
                      i18n.language === 'zh-CN' ? 'zh-CN' : 'en-US'
                    )
                  : t('questionnaire:statusPage.justNow')}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('questionnaire:statusPage.currentStatus')}
              >
                <Tag color="blue" icon={<ClockCircleOutlined />}>
                  {t('questionnaire:statusPage.pendingReview')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item
                label={t('questionnaire:statusPage.estimatedReviewTime')}
                span={2}
              >
                {t('questionnaire:statusPage.reviewDays')}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('questionnaire:statusPage.notificationMethod')}
                span={2}
              >
                <div className="flex items-center gap-2">
                  <MailOutlined />
                  <span>{t('questionnaire:statusPage.emailNotification')}</span>
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">
              📋 {t('questionnaire:statusPage.nextSteps')}
            </h4>
            <div className="space-y-2 text-sm text-blue-700">
              <div>1. {t('questionnaire:statusPage.step1')}</div>
              <div>2. {t('questionnaire:statusPage.step2')}</div>
              <div>3. {t('questionnaire:statusPage.step3')}</div>
              <div>4. {t('questionnaire:statusPage.step4')}</div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2">
              📞 {t('questionnaire:statusPage.contactInfo')}
            </h4>
            <div className="space-y-1 text-sm text-gray-600">
              <div>
                {t('questionnaire:statusPage.supportEmail')}:{' '}
                {CONTACT_CONFIG.supportEmail}
              </div>
              <div>
                {t('questionnaire:statusPage.serviceHotline')}:{' '}
                {CONTACT_CONFIG.serviceHotline}
              </div>
              <div>
                {t('questionnaire:statusPage.workingHours')}:{' '}
                {CONTACT_CONFIG.workingHours}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QuestionnaireStatusPage;
